import React, { useState } from 'react';
import {
  Container,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useInstitution } from '../../context/InstitutionContext';

export default function AddClass() {
  const navigate = useNavigate();
  const { currentInstitution, createClass, loading } = useInstitution();
  
  const [formData, setFormData] = useState({
    name: '',
    class_number: 1,
    section: 'A',
    total_strength: 45,
    board: 'CBSE',
    academic_year: '2024-2025',
    class_teacher_id: '',
    classroom_number: ''
  });

  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'class_number' || name === 'total_strength' ? parseInt(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      if (!formData.name.trim()) {
        setError('Class name is required');
        return;
      }

      if (!currentInstitution?.id) {
        setError('Institution not found');
        return;
      }

      await createClass(currentInstitution.id, formData);
      
      setFormData({
        name: '',
        class_number: 1,
        section: 'A',
        total_strength: 45,
        board: 'CBSE',
        academic_year: '2024-2025',
        class_teacher_id: '',
        classroom_number: ''
      });

      navigate('/admin-dashboard');
    } catch (err) {
      console.error('Error creating class:', err);
      setError(err.message || 'Failed to create class');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Add New Class
        </Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>
          Create a new class for {currentInstitution?.name}
        </Typography>
      </Box>

      <Card>
        <CardContent sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Class Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Class 10"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="text"
                  label="Class Number"
                  name="class_number"
                  value={formData.class_number}
                  onChange={handleChange}
                  inputProps={{ min: 1, max: 12 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Section"
                  name="section"
                  value={formData.section}
                  onChange={handleChange}
                  placeholder="e.g., A"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="text"
                  label="Total Strength"
                  name="total_strength"
                  value={formData.total_strength}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Board"
                  name="board"
                  value={formData.board}
                  onChange={handleChange}
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="CBSE">CBSE</option>
                  <option value="ICSE">ICSE</option>
                  <option value="State">State Board</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Academic Year"
                  name="academic_year"
                  value={formData.academic_year}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Class Teacher ID (Optional)"
                  name="class_teacher_id"
                  value={formData.class_teacher_id}
                  onChange={handleChange}
                  placeholder="Primary teacher"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Classroom Number"
                  name="classroom_number"
                  value={formData.classroom_number}
                  onChange={handleChange}
                  placeholder="e.g., 10-A"
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/admin-dashboard')}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={submitting}
              >
                {submitting ? <CircularProgress size={24} /> : 'Create Class'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
}
